import { Moment } from 'moment';
import {
  Currency,
  IFormatSetting,
  Locale,
  TimeFormat,
} from '../apollo/graphqlSchema';
import moment from 'moment/moment';
import { assertCondition } from '../types';
import {
  DisplayDateFormat,
  ES5NumberFormatOption,
  INumberFormatOptionProps,
} from './types';
import { INTERNAL_DATE_FORMAT } from '../../config/constants';
import { localeDictionary, timeFormatDictionary } from './utils';
import getCurrencySymbol from './getCurrencySymbol';

/**
 * GlobalFormat is a class that provides a global formatting utility for the entire application.
 * It is used to format numbers, dates, and times in a consistent manner.
 * It also provides a way to update the global format configuration.
 */
class GlobalFormat {
  private formatConfig: Omit<IFormatSetting, '__typename'> & {
    date: DisplayDateFormat;
  };

  constructor() {
    this.formatConfig = {
      locale: Locale.EnUs,
      currency: Currency.Usd,
      time: TimeFormat.TwentyFourHour,
      date: INTERNAL_DATE_FORMAT,
    };
  }

  public getGlobalFormat = () => {
    return {
      dateFormat: this.formatConfig.date,
      timeFormat: this.formatConfig.time,
      currency: this.formatConfig.currency,
      locale: this.formatConfig.locale,
    };
  };

  public getFormatCurrencySymbol = () => {
    return getCurrencySymbol(
      this.formatConfig.currency,
      this.formatConfig.locale,
    );
  };

  public updateFormatConfig = (formatSetting: IFormatSetting) => {
    // type check for date format, only support such 4 date formats right now
    // and then cast it to DisplayDateFormat
    assertCondition(
      formatSetting.date === 'YYYY-MM-DD' ||
        formatSetting.date === 'MM/DD/YYYY' ||
        formatSetting.date === 'DD/MM/YYYY' ||
        formatSetting.date === 'DD.MM.YYYY',
    );
    this.formatConfig = {
      ...formatSetting,
      date: formatSetting.date satisfies DisplayDateFormat,
    };
  };

  /**
   * formatNumber is a function that formats a number into a string with the specified format.
   * refer to
   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat
   */
  public formatNumber = (
    value: number | string,
    { decimalPlace, locale, ...numberFormatOptions }: INumberFormatOptionProps,
  ) => {
    // {min, max} decimal config has higher priority than decimalPlace
    const formatOptions: ES5NumberFormatOption = {
      ...numberFormatOptions,
      minimumFractionDigits:
        numberFormatOptions?.minimumFractionDigits ?? decimalPlace,
      maximumFractionDigits:
        numberFormatOptions?.maximumFractionDigits ?? decimalPlace,
    };
    let inputLocale: string =
      localeDictionary[locale ?? this.formatConfig.locale];
    const numberValue = Number(value);
    if (isNaN(numberValue)) {
      console.warn('Invalid value on number formatting: ', value);
      return value.toString();
    }
    // if the number is 0, we want to remove the decimal places and display 0 directly
    if (numberValue === 0) {
      formatOptions.minimumFractionDigits = 0;
      formatOptions.maximumFractionDigits = 0;
    }
    if (numberFormatOptions.style === 'currency') {
      formatOptions.currency = this.formatConfig.currency;
      formatOptions.currencyDisplay = 'narrowSymbol';
    }

    // if the notation is compact, we want to use en-US locale
    // otherwise, we will have all kinds different abbr for K,M,B,T from diff languages
    if (numberFormatOptions.notation === 'compact') {
      inputLocale = localeDictionary[Locale.EnUs];
    }
    return new Intl.NumberFormat(inputLocale, formatOptions).format(
      numberValue,
    );
  };

  /**
   * formatDate is a function that formats a date object or string into a string with the specified format.
   * But because date format is more flexible config which can combine with diff formats template,
   * therefore we need to pass the format as an object or a combined format string
   * without predefined options like number format
   * @param date unformatted date
   * @param format {dateFormat, timeFormat} usually is loaded from getGlobalDateFormat(),
   *        or a combined format string like 'DD.MM.YYYY HH:mm:ss'
   * @param sourceFormat original format of the date string, it will use strict rule to parse the date string
   */
  public formatDate = (
    date: Date | string | Moment,
    format:
      | {
          dateFormat: DisplayDateFormat;
          timeFormat?: TimeFormat;
        }
      | string,
    sourceFormat?: string,
  ): string => {
    const targetFormat =
      typeof format === 'string'
        ? format
        : format.dateFormat +
          (format.timeFormat
            ? ` ${timeFormatDictionary[format.timeFormat]}`
            : '');

    if (typeof date === 'string' && sourceFormat) {
      return moment(date, sourceFormat, true).format(targetFormat);
    } else {
      return moment(date).format(targetFormat);
    }
  };

  public formatTime = (
    time: Date | string | Moment,
    timeFormat: TimeFormat,
  ): string => {
    return moment(time).format(timeFormatDictionary[timeFormat]);
  };

  public formatCurrency = ({
    value,
    locale,
    currency,
  }: {
    value: number;
    locale: Locale;
    currency: Currency;
  }): string => {
    return new Intl.NumberFormat(localeDictionary[locale], {
      style: 'currency',
      currency,
    }).format(value);
  };
}

const globalFormat = new GlobalFormat();

export default globalFormat;
